// Identity documents as a screen may show them: a CPF or CNPJ keeps only its middle characters, any
// other document number only its middle third. `maskedDocuments` masks every document a stored OPIN
// payload carries before it leaves its owner (LGPD): by the member that names one, by any type
// member beside a generic identifier, and by a CPF or CNPJ's shape in any text.
import { Option, Predicate } from 'effect';
import type { Schema } from 'effect';
import { validateCpfDigits } from '../../../schemas/br/core/validators.ts';
import { CNPJ_PATTERN, hasCnpjCheckDigits } from './cnpj.ts';
import { jsonObjectOf } from '../../json/json.ts';

const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;
const GROUP = 3;
// The middle of a document is all a screen shows of it.
const CPF_SHOWN = { from: 3, to: 9 } as const;
const CNPJ_SHOWN = { from: 2, to: 8 } as const;

/** A CPF's 11 digits as a screen shows them: only its middle digits. */
export const maskCpf = (cpf: string): string => {
  if (cpf.length !== CPF_LENGTH) return '***.***.***-**';
  const middle = cpf.slice(CPF_SHOWN.from, CPF_SHOWN.to);
  return `***.${middle.slice(0, GROUP)}.${middle.slice(GROUP)}-**`;
};

/** A CNPJ's 14 characters as a screen shows them: only its middle characters. */
export const maskCnpj = (cnpj: string): string => {
  if (cnpj.length !== CNPJ_LENGTH) return '**.***.***/****-**';
  const middle = cnpj.slice(CNPJ_SHOWN.from, CNPJ_SHOWN.to);
  return `**.${middle.slice(0, GROUP)}.${middle.slice(GROUP)}/****-**`;
};

const DOCUMENT_CHARACTER = /[\dA-Za-z]/u;

// Any other document number (RG, passport, ...): only its middle third shows, punctuation kept.
const maskOther = (value: string): string => {
  const characters = Array.from(value.matchAll(/[\s\S]/gu), ([character]) => character);
  const total = characters.filter((each) => DOCUMENT_CHARACTER.test(each)).length;
  const start = Math.floor(total / GROUP);
  const end = total - start;
  return characters.reduce<{ readonly seen: number; readonly text: string }>(
    ({ seen, text }, each) => {
      if (!DOCUMENT_CHARACTER.test(each)) return { seen, text: `${text}${each}` };
      const shown = seen >= start && seen < end;
      return { seen: seen + 1, text: `${text}${shown ? each : '*'}` };
    },
    { seen: 0, text: '' },
  ).text;
};

const documentCharacters = (value: string): string =>
  value.toUpperCase().replaceAll(/[^\dA-Z]/gu, '');

/** A document number of any kind, masked: a CPF or CNPJ in the screen's shape, any other in part. */
export const maskDocument = (value: string): string => {
  const characters = documentCharacters(value);
  if (/^\d{11}$/u.test(characters)) return maskCpf(characters);
  if (CNPJ_PATTERN.test(characters)) return maskCnpj(characters);
  return maskOther(value);
};

// Members that always hold a document number, whatever surrounds them.
const DOCUMENT_MEMBERS: ReadonlySet<string> = new Set([
  'cpf',
  'cnpj',
  'cpfNumber',
  'cnpjNumber',
  'businesscnpjNumber',
  'rg',
  'rgNumber',
  'passportNumber',
  'documentNumber',
  'tellerId',
  // A capitalization title's assignor (cedente), stated with no type beside it.
  'transferorId',
]);

// Generic identifier members: one beside a type member holds a document number, whatever the type
// says, so a type the receiver does not know (`RG_RE`, one OPIN adds later) is masked, never passed.
// Business identifiers (`id`, `policyId`, a claim's id) are not among them.
const IDENTIFIER_MEMBERS: ReadonlySet<string> = new Set(['identification', 'document', 'number']);
const TYPE_MEMBERS = [
  'identificationType',
  'documentType',
  'type',
  'tellerIdType',
  'idType',
] as const;
// A phone pairs a type with its number too; the operator reads phones in full.
const PHONE_MEMBERS = ['areaCode', 'countryCallingCode'] as const;

const pairsIdentifier = (object: Schema.JsonObject): boolean =>
  TYPE_MEMBERS.some((member) => Object.hasOwn(object, member)) &&
  !PHONE_MEMBERS.some((member) => Object.hasOwn(object, member));

// A CPF or CNPJ in its punctuated shape is masked as is; a bare run of 11 or 14 digits only when
// its check digits hold, so a phone or a policy number is not taken for one; an alphanumeric CNPJ
// likewise.
const PUNCTUATED_CPF = /(?<![\dA-Za-z])\d{3}\.\d{3}\.\d{3}-\d{2}(?![\dA-Za-z])/gu;
const PUNCTUATED_CNPJ =
  /(?<![\dA-Za-z])[\dA-Z]{2}\.[\dA-Z]{3}\.[\dA-Z]{3}\/[\dA-Z]{4}-\d{2}(?![\dA-Za-z])/gu;
const BARE_DIGITS = /(?<![\dA-Za-z])(?:\d{14}|\d{11})(?![\dA-Za-z])/gu;
// An alphanumeric CNPJ (Receita Federal, from July 2026) has a letter among its first 12 characters.
const BARE_ALPHANUMERIC_CNPJ = new RegExp(
  `(?<![\\dA-Za-z])(?=[\\dA-Z]{0,11}[A-Z])${CNPJ_PATTERN.source.slice(1, -1)}(?![\\dA-Za-z])`,
  'gu',
);

const bareDocument = (digits: string): string => {
  if (digits.length === CPF_LENGTH) return validateCpfDigits(digits) ? maskCpf(digits) : digits;
  return hasCnpjCheckDigits(digits) ? maskCnpj(digits) : digits;
};

/** `text` with every CPF or CNPJ shape it holds masked. */
export const maskDocumentsInText = (text: string): string =>
  text
    .replaceAll(PUNCTUATED_CPF, (cpf) => maskCpf(documentCharacters(cpf)))
    .replaceAll(PUNCTUATED_CNPJ, (cnpj) => maskCnpj(documentCharacters(cnpj)))
    .replaceAll(BARE_DIGITS, bareDocument)
    .replaceAll(BARE_ALPHANUMERIC_CNPJ, (cnpj) =>
      hasCnpjCheckDigits(cnpj) ? maskCnpj(cnpj) : cnpj,
    );

// A document sent as a JSON number is masked as its digits; any other number stays a number unless
// it is a CPF or CNPJ whose check digits hold.
const maskedNumber = (value: number): Schema.Json => {
  const masked = maskDocumentsInText(String(value));
  return masked === String(value) ? value : masked;
};

// Whether the member holds a document number: by its own name, or an identifier beside a type.
const holdsDocument = (object: Schema.JsonObject, key: string, typed: boolean): boolean =>
  DOCUMENT_MEMBERS.has(key) || (typed && IDENTIFIER_MEMBERS.has(key)) || namesItsType(object, key);

// A member stated beside its own type is an identifier whatever its name: `surveyorId` beside
// `surveyorIdType`, `beneficiaryDocument` beside `beneficiaryDocumentType`, `holderDocumentNumber`
// beside `holderDocumentType` (OPIN's pairs), so a pair OPIN adds later is masked too.
const namesItsType = (object: Schema.JsonObject, key: string): boolean =>
  Object.hasOwn(object, `${key}Type`) ||
  (key.endsWith('Number') && Object.hasOwn(object, `${key.slice(0, -'Number'.length)}Type`));

// A member named for a document is masked whole, whatever JSON kind it was sent as.
const maskedDocumentMember = (value: Schema.Json): Schema.Json =>
  Predicate.isString(value) || Predicate.isNumber(value)
    ? maskDocument(String(value))
    : maskedDocuments(value);

// The lists whose items are things, not people: an insured object pairs its own identifier (a
// plate, a property registration) with its kind (`type: 'AUTOMOVEL'`), so that pair is kept. A
// CPF or CNPJ there is still masked by its shape, and everything nested in the item as usual.
const OBJECT_LISTS: ReadonlySet<string> = new Set(['insuredObjects']);

// An object's members masked; `pairing` off keeps its own identifier beside its type.
const maskedObject = (object: Schema.JsonObject, pairing: boolean): Schema.Json => {
  const typed = pairing && pairsIdentifier(object);
  return Object.fromEntries(
    Object.entries(object).map(([key, member]) => [key, maskedMember(object, key, member, typed)]),
  );
};

// An item of a list of things: its own identifier kept, all it nests masked.
const maskedThing = (value: Schema.Json): Schema.Json =>
  Option.match(jsonObjectOf(value), {
    onNone: () => maskedDocuments(value),
    onSome: (object) => maskedObject(object, false),
  });

const maskedMember = (
  object: Schema.JsonObject,
  key: string,
  value: Schema.Json,
  typed: boolean,
): Schema.Json => {
  if (holdsDocument(object, key, typed)) return maskedDocumentMember(value);
  if (OBJECT_LISTS.has(key) && Array.isArray(value)) {
    return value.map((each: Schema.Json) => maskedThing(each));
  }
  return maskedDocuments(value);
};

/** `value` with every identity document it carries masked, at any depth; nothing else changes. */
export const maskedDocuments = (value: Schema.Json): Schema.Json => {
  if (Predicate.isString(value)) return maskDocumentsInText(value);
  if (Predicate.isNumber(value)) return maskedNumber(value);
  if (Array.isArray(value)) return value.map((each: Schema.Json) => maskedDocuments(each));
  return Option.match(jsonObjectOf(value), {
    onNone: () => value,
    onSome: (object) => maskedObject(object, true),
  });
};
